import { IBoundingBox } from "./IBoundingBox.js";
import { IMatrix4 } from "./IMatrix4.js";
import { IPlane } from "./IPlane.js";
import { ITransformedBoundingBox } from "./ITransformedBoundingBox.js";
import { IVector3 } from "./IVector3.js";
export class IFrustum {
    constructor(...args // 6 args
    ) {
        function normalize_plane(plane) {
            const length = IVector3.magnitude(plane.normal);
            IVector3.scale_in_place(plane.normal, -1 / length);
            plane.d /= -length;
            return plane;
        }
        if (args.length === 2) {
            // Based on https://www.gamedevs.org/uploads/fast-extraction-viewing-frustum-planes-from-world-view-projection-matrix.pdf
            // Gribb Hartmann: Fast Extraction of Viewing Frustum Planes from the World-View-Projection Matrix
            const view_projection = IMatrix4.multiply(args[1], args[0]);
            const frustum = {
                left: new IPlane(view_projection[0].w + view_projection[0].x, view_projection[1].w + view_projection[1].x, view_projection[2].w + view_projection[2].x, view_projection[3].w + view_projection[3].x),
                right: new IPlane(view_projection[0].w - view_projection[0].x, view_projection[1].w - view_projection[1].x, view_projection[2].w - view_projection[2].x, view_projection[3].w - view_projection[3].x),
                top: new IPlane(view_projection[0].w - view_projection[0].y, view_projection[1].w - view_projection[1].y, view_projection[2].w - view_projection[2].y, view_projection[3].w - view_projection[3].y),
                bottom: new IPlane(view_projection[0].w + view_projection[0].y, view_projection[1].w + view_projection[1].y, view_projection[2].w + view_projection[2].y, view_projection[3].w + view_projection[3].y),
                near: new IPlane(view_projection[0].w + view_projection[0].z, view_projection[1].w + view_projection[1].z, view_projection[2].w + view_projection[2].z, view_projection[3].w + view_projection[3].z),
                far: new IPlane(view_projection[0].w - view_projection[0].z, view_projection[1].w - view_projection[1].z, view_projection[2].w - view_projection[2].z, view_projection[3].w - view_projection[3].z)
            };
            // We have to normalize the planes
            normalize_plane(frustum.far);
            normalize_plane(frustum.near);
            normalize_plane(frustum.top);
            normalize_plane(frustum.bottom);
            normalize_plane(frustum.left);
            normalize_plane(frustum.right);
            return frustum;
        }
        else {
            return {
                left: IPlane.copy(args[0]),
                right: IPlane.copy(args[1]),
                top: IPlane.copy(args[2]),
                bottom: IPlane.copy(args[3]),
                far: IPlane.copy(args[4]),
                near: IPlane.copy(args[5])
            };
        }
    }
    static planes_array(frustum) {
        // Do not change order of this array since others functions can rely on it
        return [frustum.left, frustum.right, frustum.top, frustum.bottom, frustum.far, frustum.near];
    }
    static intersects_point(frustum, point) {
        return !(IPlane.point_above_plane(frustum.far, point) > 0 ||
            IPlane.point_above_plane(frustum.near, point) > 0 ||
            IPlane.point_above_plane(frustum.left, point) > 0 ||
            IPlane.point_above_plane(frustum.right, point) > 0 ||
            IPlane.point_above_plane(frustum.top, point) > 0 ||
            IPlane.point_above_plane(frustum.bottom, point) > 0);
    }
    static intersects_sphere(frustum, sphere) {
        return !(IPlane.sphere_above_plane(frustum.far, sphere) ||
            IPlane.sphere_above_plane(frustum.near, sphere) ||
            IPlane.sphere_above_plane(frustum.left, sphere) ||
            IPlane.sphere_above_plane(frustum.right, sphere) ||
            IPlane.sphere_above_plane(frustum.top, sphere) ||
            IPlane.sphere_above_plane(frustum.bottom, sphere));
    }
    static intersects_box(frustum, box) {
        // Check if all corners are on the same side of each plane
        if (IBoundingBox.is(box)) {
            box = { local_bounding_box: box, transform: new IMatrix4() };
        }
        const box_corners = ITransformedBoundingBox.get_transformed_vertices(box);
        const planes = IFrustum.planes_array(frustum);
        for (let i = 0; i < planes.length; i++) {
            const plane = planes[i];
            let all_positive = 0;
            for (let j = 0; j < box_corners.length; j++) {
                const corner = box_corners[j];
                all_positive += IPlane.point_above_plane(plane, corner) > 0 ? 1 : 0;
            }
            if (all_positive === 8)
                return false;
        }
        // Update caches
        IPlane.intersection_corner_to_ref(frustum.left, frustum.top, frustum.far, IFrustum._frustum_corners[0]);
        IPlane.intersection_corner_to_ref(frustum.left, frustum.bottom, frustum.far, IFrustum._frustum_corners[1]);
        IPlane.intersection_corner_to_ref(frustum.left, frustum.bottom, frustum.near, IFrustum._frustum_corners[2]);
        IPlane.intersection_corner_to_ref(frustum.left, frustum.top, frustum.near, IFrustum._frustum_corners[3]);
        IPlane.intersection_corner_to_ref(frustum.right, frustum.top, frustum.far, IFrustum._frustum_corners[4]);
        IPlane.intersection_corner_to_ref(frustum.right, frustum.bottom, frustum.far, IFrustum._frustum_corners[5]);
        IPlane.intersection_corner_to_ref(frustum.right, frustum.bottom, frustum.near, IFrustum._frustum_corners[6]);
        IPlane.intersection_corner_to_ref(frustum.right, frustum.top, frustum.near, IFrustum._frustum_corners[7]);
        IPlane.set(IFrustum._box_planes[0], box_corners[0], box_corners[2], box_corners[1]);
        IPlane.set(IFrustum._box_planes[1], box_corners[0], box_corners[4], box_corners[2]);
        IPlane.set(IFrustum._box_planes[2], box_corners[1], box_corners[3], box_corners[5]);
        IPlane.set(IFrustum._box_planes[3], box_corners[3], box_corners[6], box_corners[7]);
        IPlane.set(IFrustum._box_planes[4], box_corners[4], box_corners[7], box_corners[6]);
        IPlane.set(IFrustum._box_planes[5], box_corners[5], box_corners[0], box_corners[1]);
        // Reverse test between frustum and box
        for (let i = 0; i < IFrustum._box_planes.length; i++) {
            const plane = IFrustum._box_planes[i];
            let all_positive = 0;
            for (let j = 0; j < IFrustum._frustum_corners.length; j++) {
                const corner = IFrustum._frustum_corners[j];
                all_positive += IPlane.point_above_plane(plane, corner) > 0 ? 1 : 0;
            }
            if (all_positive === 8)
                return false;
        }
        // If both our checks pass, we have a hit
        return true;
    }
}
IFrustum._box_planes = [
    new IPlane(0, 0, 0, 0),
    new IPlane(0, 0, 0, 0),
    new IPlane(0, 0, 0, 0),
    new IPlane(0, 0, 0, 0),
    new IPlane(0, 0, 0, 0),
    new IPlane(0, 0, 0, 0)
];
IFrustum._frustum_corners = [
    new IVector3(),
    new IVector3(),
    new IVector3(),
    new IVector3(),
    new IVector3(),
    new IVector3(),
    new IVector3(),
    new IVector3()
];
//# sourceMappingURL=IFrustum.js.map